import React, { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useRevokeConsentMutation } from "@/store/rest_api";
import type ContactPhone from "@/model/interface/ContactPhone";

const revokeConsentSchema = z.object({
  consentType: z.string().min(1),
  source: z.string().min(1),
  phoneNumberE164: z.string().optional(),
  notes: z.string().optional(),
});

type RevokeConsentFormValues = z.infer<typeof revokeConsentSchema>;

interface RevokeConsentDialogProps {
  contactId: string;
  phones?: ContactPhone[];
  consentStatus?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RevokeConsentDialog: React.FC<RevokeConsentDialogProps> = ({ contactId, phones, consentStatus, open, onOpenChange }) => {
  const [revokeConsent, { isLoading }] = useRevokeConsentMutation();

  const form = useForm<RevokeConsentFormValues>({
    resolver: zodResolver(revokeConsentSchema),
    defaultValues: {
      consentType: "",
      source: "MANUAL",
      phoneNumberE164: "",
      notes: "",
    },
  });

  const selectedPhone = form.watch("phoneNumberE164");

  // Determine which consent types can be revoked based on selected phone (or all phones)
  const revocableTypes = useMemo(() => {
    const types = new Set<string>();
    const relevantPhones = selectedPhone && selectedPhone !== "-"
      ? phones?.filter((phone) => (phone.phoneNumberE164 || phone.phoneNumber) === selectedPhone)
      : phones;

    relevantPhones?.forEach((phone) => {
      const ct = phone.consentType;
      if (ct === "CALL" || ct === "CALL_AND_SMS") types.add("CALL");
      if (ct === "SMS" || ct === "CALL_AND_SMS") types.add("SMS");
      if (ct === "CALL_AND_SMS") types.add("CALL_AND_SMS");
    });

    if (types.size === 0 && (consentStatus === "EXPRESS" || consentStatus === "WRITTEN")) {
      types.add("CALL");
      types.add("SMS");
      types.add("CALL_AND_SMS");
    }

    return types;
  }, [phones, selectedPhone, consentStatus]);

  useEffect(() => {
    if (open) {
      const phonesWithConsent = phones?.filter((phone) => phone.consentType && phone.consentType !== "NONE") ?? [];
      const firstPhone = phonesWithConsent[0];
      const preselectedPhone = phonesWithConsent.length === 1
        ? (firstPhone.phoneNumberE164 || firstPhone.phoneNumber || "")
        : "";
      form.reset({
        consentType: "",
        source: "MANUAL",
        phoneNumberE164: preselectedPhone,
        notes: "",
      });
    }
  }, [open]);

  // Reset consent type when it's no longer valid for the selected phone
  const currentConsentType = form.watch("consentType");
  useEffect(() => {
    if (currentConsentType && !revocableTypes.has(currentConsentType)) {
      form.setValue("consentType", "");
    }
  }, [revocableTypes, currentConsentType]);

  const onSubmit = async (values: RevokeConsentFormValues) => {
    try {
      await revokeConsent({
        contactId,
        consentType: values.consentType,
        source: values.source,
        phoneNumberE164: values.phoneNumberE164 && values.phoneNumberE164 !== "-" ? values.phoneNumberE164 : undefined,
        notes: values.notes?.trim() || undefined,
      }).unwrap();
      toast.success("Consent revoked");
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to revoke consent");
    }
  };

  const CONSENT_LABELS: Record<string, string> = {
    CALL: "Calling",
    SMS: "SMS",
    CALL_AND_SMS: "Calling & SMS",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke Consent</DialogTitle>
        </DialogHeader>
        {revocableTypes.size === 0 ? (
          <>
            <p className="text-sm text-muted-foreground">This contact has no active consent to revoke.</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="phoneNumberE164" render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger><SelectValue placeholder="All phone numbers" /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="-">All phone numbers</SelectItem>
                    {phones?.filter((phone) => phone.consentType && phone.consentType !== "NONE").map((phone) => {
                      const num = phone.phoneNumberE164 || phone.phoneNumber || "";
                      return (
                        <SelectItem key={num} value={num}>{num}</SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            <FormField control={form.control} name="consentType" render={({ field }) => (
              <FormItem>
                <FormLabel>Consent Type</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Select type to revoke" /></SelectTrigger></FormControl>
                  <SelectContent>
                    {["CALL", "SMS", "CALL_AND_SMS"].filter((t) => revocableTypes.has(t)).map((t) => (
                      <SelectItem key={t} value={t}>{CONSENT_LABELS[t]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            <FormField control={form.control} name="source" render={({ field }) => (
              <FormItem>
                <FormLabel>Source</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="MANUAL">Manual</SelectItem>
                    <SelectItem value="VERBAL">Verbal</SelectItem>
                    <SelectItem value="WEB_FORM">Web Form</SelectItem>
                    <SelectItem value="IMPORT">Import</SelectItem>
                    <SelectItem value="INTEGRATION">Integration</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            <FormField control={form.control} name="notes" render={({ field }) => (
              <FormItem>
                <FormLabel>Notes (optional)</FormLabel>
                <FormControl><Textarea rows={2} placeholder="Reason for revocation..." {...field} /></FormControl>
              </FormItem>
            )} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={isLoading || revocableTypes.size === 0}>
                {isLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Revoke Consent
              </Button>
            </DialogFooter>
          </form>
        </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RevokeConsentDialog;
